import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Chip, { type ChipProps } from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Popover from "@mui/material/Popover";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import React from "react";
import { useTranslation } from "react-i18next";
import TimeAgo from "react-timeago-i18n";
import axios from "@/common/axios";
import type { PersonId } from "@/stores/definitions/Person";
import * as Parsers from "../util/parsers";

type UserDetailsArgs = {
  userId: PersonId;
  fullName: string;
  position: ["top" | "bottom", "right" | "left"];
  label?: string;
  variant?: ChipProps["variant"];
  allowMessaging?: boolean;
  onOpen?: () => void;
};

type Group = {
  groupId: number;
  roleInGroup: "PI" | "User";
  groupName: string;
};
type Person = {
  groups: Array<Group>;
  lastLogin: string;
  fullname: string;
  username?: string;
  profileImageLink: string;
  email: string;
  accountEnabled: boolean;
};

const OPEN_CREATE_REQUEST_DIALOG_EVENT = "OPEN_CREATE_REQUEST_DIALOG";

function getCreateRequestDialog(): HTMLElement | null {
  const dialog = document.getElementById("createRequestDlg");

  return dialog instanceof HTMLElement ? dialog : null;
}

function canOpenMessageDialog(): boolean {
  return Boolean(getCreateRequestDialog() && document.body.querySelector("[aria-describedby='messageDlg']"));
}

function openCreateRequestDialog(recipient: string): void {
  const dialog = getCreateRequestDialog();

  if (!dialog) {
    return;
  }

  dialog.dispatchEvent(
    new CustomEvent<{ recipient: string }>(OPEN_CREATE_REQUEST_DIALOG_EVENT, {
      bubbles: true,
      detail: { recipient },
    }),
  );
}

export default function UserDetails(props: UserDetailsArgs): React.ReactNode {
  const { t } = useTranslation("common");
  const variant = props.variant ?? "filled";
  const [anchorEl, setAnchorEl] = React.useState<null | Element>(null);
  const [user, setUser] = React.useState<Person | null>(null);
  const [fetched, setFetched] = React.useState(false);
  const [messagingAvailable, setMessagingAvailable] = React.useState(false);

  const sendMessage = () => {
    if (!user?.username) {
      return;
    }

    setAnchorEl(null);
    const recipient = `${user.username}<${user.fullname}>,`;
    openCreateRequestDialog(recipient);
  };

  const fetchUser = () => {
    const url = `/userform/ajax/miniprofile/${props.userId}`;
    axios
      .get<{ exceptionMessage: string } | { data: Person }>(url)
      .then((response) => {
        setUser(Parsers.getValueWithKey("data")(response.data).orElse(null) as Person | null);
      })
      .catch((error) => {
        console.error(error);
      });
    setFetched(true);
  };

  /*
   * Links and buttons share a single accent colour and keep it when visited,
   * rather than falling back to the browser's default blue/purple link colours.
   */
  const accentColor = "#1465b7";
  const linkSx = {
    color: accentColor,
    "&:visited": { color: accentColor },
  };

  const listLabgroups = user?.groups.map((group) => (
    <TableRow key={group.groupId}>
      <TableCell component="th" scope="row">
        {t("userDetails.roleAt", {
          role: group.roleInGroup === "PI" ? t("userDetails.roles.pi") : t("userDetails.roles.user"),
        })}
      </TableCell>
      <TableCell align="right">
        <Link href={`/groups/view/${group.groupId}`} sx={linkSx}>
          {group.groupName}
        </Link>
      </TableCell>
    </TableRow>
    // biome-ignore lint/complexity/noUselessFragments: initial biome migration
  )) ?? <></>;

  const handlePopoverOpen = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    props.onOpen?.();
    if (!fetched) {
      fetchUser();
      if (props.allowMessaging && canOpenMessageDialog()) {
        setMessagingAvailable(true);
      }
    }
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = (event: Event) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  return (
    <>
      <Chip
        clickable={true}
        component="div"
        variant={variant}
        label={props.label ?? props.fullName}
        onClick={handlePopoverOpen}
        sx={(theme) => ({
          mt: 0.5,
          mb: 0.5,
          height: theme.spacing(3),
          cursor: "default",
          ...(variant === "filled"
            ? {
                backgroundColor: theme.palette.grey[300],
                color: theme.palette.grey[800],
                fontWeight: theme.typography.fontWeightRegular,
              }
            : {}),
        })}
      />
      <Popover
        open={Boolean(anchorEl) && Boolean(user)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: props.position[0] === "top" ? "top" : "bottom",
          horizontal: props.position[1] === "right" ? "left" : "right",
        }}
        transformOrigin={{
          vertical: props.position[0] === "top" ? "bottom" : "top",
          horizontal: props.position[1] === "right" ? "left" : "right",
        }}
        slotProps={{
          paper: {
            sx: {
              p: 0,
            },
          },
        }}
      >
        {user && (
          <Card>
            <CardHeader
              avatar={<Avatar src={user.profileImageLink ?? ""} />}
              title={`${user.fullname}`}
              subheader={
                user.lastLogin && (
                  <span>
                    {t("userDetails.lastLogin")} {<TimeAgo date={user.lastLogin} locale="en-US" />}
                  </span>
                )
              }
            />
            <CardContent
              sx={{
                p: 0,
                "&:last-child": {
                  pb: 0,
                },
              }}
            >
              <Table
                sx={{
                  // Override typo.css
                  margin: "0 !important",
                }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {t("userDetails.email")}
                    </TableCell>
                    <TableCell align="right">
                      <Link href={`mailto:${user.email}`} sx={linkSx}>
                        {user.email}
                      </Link>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {t("userDetails.accountStatus")}
                    </TableCell>
                    <TableCell align="right">
                      {user.accountEnabled ? t("userDetails.accountEnabled") : t("userDetails.accountDisabled")}
                    </TableCell>
                  </TableRow>
                  {listLabgroups}
                </TableBody>
              </Table>
            </CardContent>
            <CardActions>
              {messagingAvailable && user.username && (
                <Button
                  size="small"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    sendMessage();
                  }}
                  href="#"
                  sx={{ color: accentColor }}
                >
                  {t("userDetails.sendMessage")}
                </Button>
              )}
              <Button
                component="a"
                size="small"
                href={`/userform?userId=${props.userId}`}
                sx={{
                  cursor: "pointer",
                  color: accentColor,
                }}
              >
                {t("userDetails.openProfile")}
              </Button>
            </CardActions>
          </Card>
        )}
      </Popover>
    </>
  );
}
